import React, { useState, useEffect, useMemo } from "react";
import {
    Card,
    Form,
    Input,
    Select,
    Button,
    Row,
    Col,
    Divider,
    Typography,
    InputNumber,
    Space,
    message,
    Statistic,
    Tooltip,
    Grid
} from "antd";
import {
    CalculatorOutlined,
    FilePdfOutlined,
    ThunderboltOutlined,
    DollarOutlined,
    NodeIndexOutlined,
    InfoCircleOutlined,
    GlobalOutlined,
    EnvironmentOutlined,
    DashboardOutlined
} from "@ant-design/icons";
import api from "../services/api";
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { useBreakpoint } = Grid;

const QuotationPage = () => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [form] = Form.useForm();
    const formValues = Form.useWatch([], form);
    const [wpOptions, setWpOptions] = useState([620, 610, 600, 590, 550, 540, 450, 335]);

    const styles = {
        container: {
            padding: isMobile ? "12px" : "20px",
            minHeight: "100vh"
        },
        card: {
            borderRadius: "8px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
            marginBottom: "16px"
        },
        sectionLabel: {
            fontWeight: 600,
            marginBottom: "12px",
            display: "block",
            color: "#262626",
            borderLeft: "3px solid #1890ff",
            paddingLeft: "8px"
        },
        statRow: {
            display: "flex",
            justifyContent: "space-between",
            padding: "8px 0",
            borderBottom: "1px dashed #f0f0f0"
        },
        highlightBox: {
            background: "#f6ffed",
            padding: "12px",
            borderRadius: "8px",
            textAlign: "center",
            marginTop: "12px",
            border: "1px solid #b7eb8f"
        }
    };

    // Default values
    useEffect(() => {
        form.setFieldsValue({
            noOfPanels: 6,
            wattPeak: 540,
            type: "PMSGY",
            phase: "Single Phase",
            gstRate: 8.9,
            avgRate: 14,
            quotedPrice: 220000,
            inverterMake: `GroWatt, Solis, GoodWe 3 KW Inverter, Single Phase`,
            netMeterDesc: `Bidirectional - 5-30A `,
            genMeterDesc: `Bidirectional - 5-30A `,
            footerNote: `Contact: 
Vishal - 88300 89973 | Swati - 98196 52202 | Swapnali Adsare - 99755 71180
Email: sales.niyatimultiservices@gmail.com
`,
            comments: `Note:
* Cost without Site Visit, considering concrete roof and columns available to support the structure. 
Final cost may vary in case additional support structure is required based on site survey
* GST Rate of 8.9% is applicable as per Union Budget.
8.9% Calculation = 70% of Cost @5% + 30% of Cost@18%
*Load Extension charges of MSEB@actuals
*Loan Assistance Included
* Electricity rate of ₹14 considered. Electricity charges increase periodically and payback period 
reduces accordingly.`,
        });
    }, [form]);

    // Calculations
    const calculations = useMemo(() => {
        const noOfPanels = formValues?.noOfPanels || 0;
        const wattPeak = formValues?.wattPeak || 0;
        const type = formValues?.type || "PMSGY";
        const gstRate = formValues?.gstRate || 0;
        const quotedPrice = formValues?.quotedPrice || 0;
        const avgRate = formValues?.avgRate || 0;

        // Use nominal capacity rounded to 1 decimal place (e.g., 3.2kW instead of 3.24kW)
        const capacity = Math.round((noOfPanels * wattPeak) / 100) / 10;

        let subsidy = 0;
        if (type === "PMSGY") {
            if (capacity >= 3) {
                subsidy = 78000;
            } else if (capacity >= 2) {
                subsidy = 60000;
            } else if (capacity >= 1) {
                subsidy = 30000;
            }
        } else if (type === "Society") {
            subsidy = 18000 * capacity;
        }

        const basicCost = quotedPrice / (1 + gstRate / 100);
        const gstAmount = quotedPrice - basicCost;
        const finalCost = quotedPrice - subsidy;

        const monthlyUnits = capacity * 100;
        const dailySavings = capacity * 4 * avgRate;
        const monthlySavings = dailySavings * 30;
        const yearlySavings = monthlySavings * 12;

        const payback = yearlySavings > 0 ? (finalCost / yearlySavings).toFixed(2) : 0;

        return {
            capacity: capacity, // Use actual value for name/labels
            basicCost: basicCost,
            gstAmount: gstAmount,
            subsidy: subsidy,
            finalCost: finalCost,
            monthlyUnits: Math.round(monthlyUnits),
            dailySavings: dailySavings,
            monthlySavings: Math.round(monthlySavings),
            yearlySavings: Math.round(yearlySavings),
            payback: payback,
        };
    }, [formValues]);

    // Update inverter default when capacity or phase changes
    useEffect(() => {
        if (!formValues?.inverterMake || formValues.inverterMake.includes('KW Inverter')) {
            const cap = Math.round(parseFloat(calculations.capacity));
            const ph = formValues?.phase || "Single Phase";
            form.setFieldsValue({
                inverterMake: `GroWatt, Solis, GoodWe ${cap} KW Inverter, ${ph}`
            });
        }
    }, [calculations.capacity, formValues?.phase]);

    // Update comments default when avgRate changes
    useEffect(() => {
        const currentComments = form.getFieldValue('comments');
        const avgRate = formValues?.avgRate;

        if (avgRate === undefined || avgRate === null) return;

        if (currentComments && /Electricity rate of ₹[\d.]+/.test(currentComments)) {
            const updatedComments = currentComments.replace(/Electricity rate of ₹[\d.]+/, `Electricity rate of ₹${avgRate}`);
            if (updatedComments !== currentComments) {
                form.setFieldsValue({ comments: updatedComments });
            }
        }
    }, [formValues?.avgRate]);

    const handleGenerateExcel = async () => {
        try {
            message.loading({ content: 'Generating Offer...', key: 'quotation' });
            const payload = {
                ...formValues,
                calculations
            };

            const response = await api.post('/quotations/generate-excel', payload, {
                responseType: 'blob'
            });

            // If it's a blob and the type is json, it's likely an error
            if (response && response.type === 'application/json') {
                const text = await response.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || "Failed to generate Excel");
            }

            const fileName = `Offer_${formValues.type}_${calculations.capacity}KW.xlsx`;
            const url = window.URL.createObjectURL(new Blob([response]));
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);

            message.success({ content: "Offer generated successfully", key: 'quotation' });
        } catch (error) {
            console.error("Excel Generation error:", error);

            // Handle error when it's a Blob (from API interceptor)
            if (error instanceof Blob) {
                try {
                    const text = await error.text();
                    const errorJson = JSON.parse(text);
                    message.error({ content: errorJson.error || "Failed to generate Excel", key: 'quotation' });
                    return;
                } catch (e) {
                    // Fallback
                }
            } else if (error.response && error.response.data instanceof Blob) {
                // Secondary check for other promise rejections
                try {
                    const text = await error.response.data.text();
                    const errorJson = JSON.parse(text);
                    message.error({ content: errorJson.error || "Failed to generate Excel", key: 'quotation' });
                    return;
                } catch (e) {
                    // Fallback
                }
            }

            const errorMsg = error.error || error.message || "Failed to generate Excel";
            message.error({ content: errorMsg, key: 'quotation' });
        }
    };

    return (
        <div style={styles.container}>
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={16}>
                    <Card
                        style={styles.card}
                        bodyStyle={{ padding: isMobile ? 12 : 16 }}
                        headStyle={{ minHeight: 48, padding: '0 16px' }}
                        title={<Space><CalculatorOutlined /> Quotation Details</Space>}
                    >
                        <Form form={form} layout="vertical">
                            <Text style={styles.sectionLabel}>1. Technical Specifications</Text>
                            <Row gutter={[12, 0]}>
                                <Col xs={12} sm={6}>
                                    <Form.Item name="noOfPanels" label="No. Panels" rules={[{ required: true }]}>
                                        <InputNumber style={{ width: "100%" }} min={1} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Form.Item name="wattPeak" label="Panel Wp" rules={[{ required: true }]}>
                                        <Select
                                            showSearch
                                            placeholder="Select or type Wp"
                                            onSearch={(value) => {
                                                const numVal = parseInt(value, 10);
                                                if (numVal && !wpOptions.includes(numVal)) {
                                                    setWpOptions(prev => [...new Set([numVal, ...prev])].sort((a, b) => b - a));
                                                }
                                            }}
                                            filterOption={(input, option) =>
                                                (option?.value ?? '').toString().toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {wpOptions.map(val => (
                                                <Option key={val} value={val}>{val}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={6}>
                                    <Form.Item name="type" label="Category" rules={[{ required: true }]}>
                                        <Select>
                                            <Option value="PMSGY">Individual Residential (Subsidy)</Option>
                                            <Option value="Society">Housing Society (Subsidy)</Option>
                                            <Option value="Commercial">Commercial</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={6}>
                                    <Form.Item name="phase" label="Phase" rules={[{ required: true }]}>
                                        <Select>
                                            <Option value="Single Phase">Single</Option>
                                            <Option value="3 Phase">3-Phase</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider style={{ margin: '12px 0' }} />
                            <Text style={styles.sectionLabel}>2. Pricing & Savings</Text>
                            <Row gutter={[12, 0]}>
                                <Col xs={24} sm={10}>
                                    <Form.Item name="quotedPrice" label="Quoted Price (with GST)" rules={[{ required: true }]}>
                                        <InputNumber
                                            style={{ width: "100%" }}
                                            formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={value => value.replace(/\₹\s?|(,*)/g, '')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={7}>
                                    <Form.Item name="gstRate" label="GST Rate (%)" rules={[{ required: true }]}>
                                        <InputNumber style={{ width: "100%" }} step={0.1} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={7}>
                                    <Form.Item name="avgRate" label="Elec. Rate (₹)" rules={[{ required: true }]}>
                                        <InputNumber style={{ width: "100%" }} step={0.5} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider style={{ margin: '12px 0' }} />
                            <Text style={styles.sectionLabel}>3. Equipment & Terms</Text>
                            <Form.Item name="inverterMake" label="Inverter Model">
                                <Input />
                            </Form.Item>

                            <Row gutter={[12, 0]}>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="netMeterDesc" label="Net Meter">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="genMeterDesc" label="Gen Meter">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={[12, 0]}>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="comments" label="Notes">
                                        <TextArea rows={isMobile ? 4 : 6} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="footerNote" label="Footer">
                                        <TextArea rows={isMobile ? 4 : 6} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card
                        style={styles.card}
                        bodyStyle={{ padding: 16 }}
                        headStyle={{ minHeight: 48, padding: '0 16px' }}
                        title="Quotation Summary"
                    >
                        <div style={styles.statRow}>
                            <Text type="secondary">System Size</Text>
                            <Text strong>{calculations.capacity} kW</Text>
                        </div>
                        <div style={styles.statRow}>
                            <Text type="secondary">Monthly Gen.</Text>
                            <Text strong>{calculations.monthlyUnits} Units</Text>
                        </div>
                        <div style={styles.statRow}>
                            <Text type="secondary">Area Rqd.</Text>
                            <Text strong>{Math.round(parseFloat(calculations.capacity) * 80)} sq.ft</Text>
                        </div>
                        <Divider style={{ margin: '12px 0' }} />
                        <div style={styles.statRow}>
                            <Text type="secondary">Basic Cost</Text>
                            <Text strong>₹{Math.round(calculations.basicCost).toLocaleString()}</Text>
                        </div>
                        <div style={styles.statRow}>
                            <Text type="secondary">GST Amount</Text>
                            <Text strong>₹{Math.round(calculations.gstAmount).toLocaleString()}</Text>
                        </div>
                        {calculations.subsidy > 0 && (
                            <div style={styles.statRow}>
                                <Text type="secondary">Govt. Subsidy</Text>
                                <Text strong type="success">- ₹{calculations.subsidy.toLocaleString()}</Text>
                            </div>
                        )}
                        <div style={styles.highlightBox}>
                            <div style={{ fontSize: '12px', color: '#52c41a' }}>FINAL COST TO CUSTOMER</div>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#237804' }}>
                                ₹{Math.round(calculations.finalCost).toLocaleString()}
                            </div>
                        </div>

                        <Divider style={{ margin: '12px 0' }} />
                        <div style={styles.statRow}>
                            <Text type="secondary">Yearly Savings</Text>
                            <Text strong>₹{calculations.yearlySavings.toLocaleString()}</Text>
                        </div>
                        <div style={styles.statRow}>
                            <Text type="secondary">Payback Period</Text>
                            <Text strong style={{ color: '#1890ff' }}>{calculations.payback} Years</Text>
                        </div>

                        <Button
                            type="primary"
                            icon={<FilePdfOutlined />}
                            block
                            style={{ height: "45px", fontWeight: 600, marginTop: "12px" }}
                            onClick={handleGenerateExcel}
                        >
                            GENERATE OFFER
                        </Button>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default QuotationPage;

